import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import ProviderAgreement from '../components/ProviderAgreement';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Car, ArrowLeft, Loader2, Upload, X, Wrench, Truck, 
  User, Phone, Mail, MapPin, Clock, FileText, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mechanicSpecialties = [
  'Oil Changes', 'Brake Repair', 'Diagnostics', 'Engine Repair',
  'Transmission', 'Electrical', 'AC/Heating', 'Suspension',
  'Tire Services', 'Battery Services', 'General Maintenance'
];

const roadsideSpecialties = [
  'Towing', 'Jump Start', 'Flat Tire', 'Lockout Service',
  'Fuel Delivery', 'Winch Out', 'Battery Replacement', 'Key Replacement'
];

export default function Apply() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'mechanic';
  const navigate = useNavigate();
  
  const [providerType, setProviderType] = useState(initialType);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    experience_years: '',
    specialties: [],
    certifications: '',
    about: '',
    has_own_vehicle: true,
    has_own_tools: true,
    photo_urls: [],
    video_urls: [],
    id_document_url: '',
    insurance_url: ''
  });

  useEffect(() => {
    setProviderType(searchParams.get('type') || 'mechanic');
  }, [searchParams]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('folder', 'applications');

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (field === 'photo_urls' || field === 'video_urls') {
        setFormData(prev => ({
          ...prev,
          [field]: [...prev[field], response.data.url]
        }));
      } else {
        setFormData(prev => ({ ...prev, [field]: response.data.url }));
      }
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (field, index = null) => {
    if (index !== null) {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Require all mandatory documents before submission
    if (!formData.id_document_url) {
      toast.error('Please upload your Driver\'s License / ID document');
      return;
    }

    if (!formData.insurance_url) {
      toast.error('Please upload your Insurance document');
      return;
    }

    if (formData.photo_urls.length === 0) {
      toast.error('Please upload at least one work sample photo');
      return;
    }

    // Require agreement to be signed
    if (!agreementSigned) {
      setShowAgreement(true);
      toast.info('Please review and sign the Provider Agreement to continue');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/applications/provider`, {
        ...formData,
        provider_type: providerType,
        experience_years: parseInt(formData.experience_years) || 0,
        agreement_signed: agreementSigned
      });
      
      toast.success('Application submitted successfully! We will contact you soon.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  // Handle agreement completion
  const handleAgreementComplete = (agreementData) => {
    setAgreementSigned(true);
    setShowAgreement(false);
    toast.success('Agreement signed! You can now submit your application.');
  };

  const specialties = providerType === 'mechanic' ? mechanicSpecialties : roadsideSpecialties;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="apply-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 font-['Chivo']">Jiffys Mobile</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-['Chivo'] mb-2">
            Join Our Team
          </h1>
          <p className="text-gray-600">
            Apply to become a service provider with Jiffys Assistance
          </p>
        </div>

        {/* Provider Type Toggle */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setProviderType('mechanic')}
            className={`flex-1 p-6 rounded-lg border-2 transition-all ${
              providerType === 'mechanic'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
            data-testid="type-mechanic"
          >
            <Wrench className={`w-8 h-8 mb-2 ${providerType === 'mechanic' ? 'text-blue-600' : 'text-gray-400'}`} />
            <h3 className="font-semibold text-gray-900">Mobile Mechanic</h3>
            <p className="text-sm text-gray-500">Repairs, diagnostics, maintenance</p>
          </button>
          <button
            onClick={() => setProviderType('roadside_provider')}
            className={`flex-1 p-6 rounded-lg border-2 transition-all ${
              providerType === 'roadside_provider'
                ? 'border-red-600 bg-red-50'
                : 'border-gray-200 hover:border-red-300'
            }`}
            data-testid="type-roadside"
          >
            <Truck className={`w-8 h-8 mb-2 ${providerType === 'roadside_provider' ? 'text-red-600' : 'text-gray-400'}`} />
            <h3 className="font-semibold text-gray-900">Roadside Provider</h3>
            <p className="text-sm text-gray-500">Towing, jump start, lockout</p>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      placeholder="John Doe"
                      className="mt-1"
                      required
                      data-testid="full-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="john@example.com"
                      className="mt-1"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      className="mt-1"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="City, State"
                      className="mt-1"
                      data-testid="address-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Experience */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Experience & Skills
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="experience_years">Years of Experience</Label>
                  <Input
                    id="experience_years"
                    type="number"
                    min="0"
                    value={formData.experience_years}
                    onChange={(e) => handleChange('experience_years', e.target.value)}
                    placeholder="5"
                    className="mt-1 w-32"
                    data-testid="experience-input"
                  />
                </div>

                <div>
                  <Label>Specialties</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                    {specialties.map((specialty) => (
                      <label
                        key={specialty}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.specialties.includes(specialty)
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.specialties.includes(specialty)}
                          onCheckedChange={() => toggleSpecialty(specialty)}
                        />
                        <span className="text-sm">{specialty}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="certifications">Certifications (optional)</Label>
                  <Input
                    id="certifications"
                    value={formData.certifications}
                    onChange={(e) => handleChange('certifications', e.target.value)}
                    placeholder="ASE Certified, etc."
                    className="mt-1"
                    data-testid="certifications-input"
                  />
                </div>

                <div>
                  <Label htmlFor="about">Tell us about yourself</Label>
                  <Textarea
                    id="about"
                    value={formData.about}
                    onChange={(e) => handleChange('about', e.target.value)}
                    placeholder="Describe your experience and why you want to join..."
                    className="mt-1"
                    rows={4}
                    data-testid="about-input"
                  />
                </div>

                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.has_own_vehicle}
                      onCheckedChange={(checked) => handleChange('has_own_vehicle', checked)}
                    />
                    <span>I have my own vehicle</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.has_own_tools}
                      onCheckedChange={(checked) => handleChange('has_own_tools', checked)}
                    />
                    <span>I have my own tools</span>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Documents & Media */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documents & Photos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Photos */}
                <div>
                  <Label className="flex items-center gap-2">
                    Photos (work samples, vehicle, etc.)
                    <span className="text-red-500 text-sm">* Required</span>
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.photo_urls.map((url, idx) => (
                      <div key={url || `photo-${idx}`} className="relative w-24 h-24 rounded-lg overflow-hidden border">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeFile('photo_urls', idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <label className={`w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 ${formData.photo_urls.length === 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'photo_urls')}
                        disabled={uploading}
                      />
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                      ) : (
                        <Upload className={`w-6 h-6 ${formData.photo_urls.length === 0 ? 'text-red-400' : 'text-gray-400'}`} />
                      )}
                    </label>
                  </div>
                  {formData.photo_urls.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">At least one photo required</p>
                  )}
                </div>

                {/* Videos */}
                <div>
                  <Label>Video Introduction (optional)</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.video_urls.map((url, idx) => (
                      <div key={url || `video-${idx}`} className="relative w-32 h-24 rounded-lg overflow-hidden border bg-gray-100 flex items-center justify-center">
                        <span className="text-sm text-gray-500">Video {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeFile('video_urls', idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <label className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400">
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, 'video_urls')}
                        disabled={uploading}
                      />
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                      ) : (
                        <div className="text-center">
                          <Upload className="w-6 h-6 text-gray-400 mx-auto" />
                          <span className="text-xs text-gray-500">Add Video</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* ID Document */}
                <div>
                  <Label className="flex items-center gap-2">
                    ID Document (Driver's License)
                    <span className="text-red-500 text-sm">* Required</span>
                  </Label>
                  <div className="mt-2">
                    {formData.id_document_url ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">Document uploaded</span>
                        <button
                          type="button"
                          onClick={() => removeFile('id_document_url')}
                          className="ml-auto text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="block w-full p-4 border-2 border-dashed border-red-300 bg-red-50 rounded-lg text-center cursor-pointer hover:border-blue-400">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'id_document_url')}
                          disabled={uploading}
                        />
                        <Upload className="w-6 h-6 text-red-400 mx-auto mb-2" />
                        <span className="text-sm text-red-500">Upload ID Document (Required)</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Insurance */}
                <div>
                  <Label className="flex items-center gap-2">
                    Insurance Document
                    <span className="text-red-500 text-sm">* Required</span>
                  </Label>
                  <div className="mt-2">
                    {formData.insurance_url ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">Insurance uploaded</span>
                        <button
                          type="button"
                          onClick={() => removeFile('insurance_url')}
                          className="ml-auto text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="block w-full p-4 border-2 border-dashed border-red-300 bg-red-50 rounded-lg text-center cursor-pointer hover:border-blue-400">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'insurance_url')}
                          disabled={uploading}
                        />
                        <Upload className="w-6 h-6 text-red-400 mx-auto mb-2" />
                        <span className="text-sm text-red-500">Upload Insurance (Required)</span>
                      </label>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Agreement Status */}
            <Card className={`border-2 ${agreementSigned ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className={`w-6 h-6 ${agreementSigned ? 'text-green-600' : 'text-amber-600'}`} />
                    <div>
                      <p className="font-semibold text-gray-900">
                        {agreementSigned ? 'Agreement Signed' : 'Provider Agreement Required'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {agreementSigned 
                          ? 'You have signed the Independent Contractor Agreement'
                          : 'Please review and sign our 30-page legal agreement'}
                      </p>
                    </div>
                  </div>
                  {!agreementSigned && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAgreement(true)}
                      data-testid="review-agreement-btn"
                    >
                      Review Agreement
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Button
              type="submit"
              className={`w-full h-14 text-lg font-semibold ${
                providerType === 'mechanic' 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              disabled={loading || !agreementSigned}
              data-testid="submit-application"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : !agreementSigned ? (
                'Sign Agreement to Continue'
              ) : (
                `Submit ${providerType === 'mechanic' ? 'Mechanic' : 'Roadside Provider'} Application`
              )}
            </Button>
          </div>
        </form>

        {/* Agreement Modal */}
        {showAgreement && (
          <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto py-8">
            <div className="max-w-4xl mx-auto px-4">
              <div className="bg-white rounded-lg shadow-xl">
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-xl font-bold">Provider Agreement</h2>
                  <button
                    onClick={() => setShowAgreement(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="p-4">
                  <ProviderAgreement 
                    onComplete={handleAgreementComplete}
                    providerId={`pending_${Date.now()}`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
